import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {SelectionItem} from '../selections/selection-item';
import {GoogleService} from './google-service';
import {PersonLoginService} from './person-login-service';
import {BrandID} from "../enums/brand-id";
import {ProjectProductType} from "../enums/project-product-type";

@Service()
export class SelectionService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);
  private readonly personLoginService = inject(PersonLoginService);


  private urlBase = environment.serviceUrl + 'selections/';

  public getProductTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("productTypes");
  }

  private getSelectionList(path: string): Observable<SelectionItem[]> {
    let headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    var url = this.urlBase + path;
    return this.http.get<SelectionItem[]>(url, {headers: headers}).pipe(map(result => {
      result.sort((first, second) => this.sortByStringProperty(first.description, second.description));
      return result;
    }));
  }

  public getCommodities(): Observable<SelectionItem[]> {
    return this.getSelectionList("commodities");
  }

  public getProductLines(brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("productLines/" + brandID);
  }

  public getProductClassCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("productClassCodes");
  }

  public getProductModels(brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("productModels/" + brandID);
  }

  public getProductLeadTimes(): Observable<SelectionItem[]> {
    const headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    const url = this.urlBase + 'productLeadTimes';
    return this.http.get<SelectionItem[]>(url, {headers: headers});
  }

  public getUomCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("uomCodes");
  }

  public getOrganizationTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("organizationTypes");
  }

  public getControllingOrgs(): Observable<SelectionItem[]> {
    return this.getSelectionList("controllingOrgs");
  }

  public getContractTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("contractTypes");
  }

  public getActiveRegionOrgs(): Observable<SelectionItem[]> {
    return this.getSelectionList("regionOrgs");
  }

  public getActiveRepOffices(): Observable<SelectionItem[]> {
    return this.getSelectionList("repOffices");
  }

  public getRegionalSalesManagers(): Observable<SelectionItem[]> {
    return this.getSelectionList("regionalSalesManagers");
  }

  public getCustomerServiceReps(): Observable<SelectionItem[]> {
    return this.getSelectionList("customerServiceReps");
  }

  public getPartsCustomerServiceReps(): Observable<SelectionItem[]> {
    return this.getSelectionList("partsCustomerServiceReps");
  }

  public getPriceLists(brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("priceLists/" + brandID);
  }

  public getVendorStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("vendorStatus");
  }

  public getPersonStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("personStatus");
  }

  public getPaymentTerms(): Observable<SelectionItem[]> {
    return this.getSelectionList("paymentTerms");
  }

  public getDeliveryTerms(): Observable<SelectionItem[]> {
    return this.getSelectionList("deliveryTerms");
  }

  public getTransportationTerms(): Observable<SelectionItem[]> {
    return this.getSelectionList("transportationTerms");
  }

  public getFreightTerms(): Observable<SelectionItem[]> {
    return this.getSelectionList("freightTerms");
  }

  public getRegionalTeams(): Observable<SelectionItem[]> {
    return this.getSelectionList("regionalTeams");
  }

  public getOrgStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("orgStatus");
  }

  public getSiteStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("siteStatus");
  }

  public getCustomerStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("customerStatus");
  }

  public getChannelStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("channelStatus");
  }

  public getOpportunityStatusCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("opportunityStatuses");
  }

  public getOpportunityPhaseCodes(): Observable<SelectionItem[]> {
    let headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    var url = this.urlBase + 'opportunityPhases';
    return this.http.get<SelectionItem[]>(url, {headers: headers}).pipe(map(result => {
      result.sort((first, second) => this.sortByNumericPrefix(first.description, second.description, '-'));
      return result;
    }));
  }

  private sortByNumericPrefix(first: string, second: string, delimiter: string): number {
    let firstNumber: number = +first.substring(0, first.indexOf(delimiter)).trim();
    let secondNumber: number = +second.substring(0, second.indexOf(delimiter)).trim();
    return firstNumber - secondNumber;
  }

  public getStateCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("stateCodes");
  }

  public getCountryCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("countryCodes");
  }

  public getAddressTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("addressTypes");
  }

  public getPhoneTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("phoneTypes");
  }

  public getEmailTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("emailTypes");
  }

  public getPersonTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("personTypes");
  }

  public getUserTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("userTypes");
  }

  public getCreditCheckMethods(): Observable<SelectionItem[]> {
    return this.getSelectionList("creditCheckMethods");
  }

  public getCreditLevelChecks(): Observable<SelectionItem[]> {
    return this.getSelectionList("creditLevelChecks");
  }

  public getCustomers(parentId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("customers/" + parentId);
  }

  public getOrderTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("orderTypes");
  }

  public getOrganizationPeople(organizationId: string, brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("organizationPeople/" + organizationId + "/" + brandID);
  }

  public getOrganizationLeadTimes(organizationId: string, brandID: BrandID, projectProductType: ProjectProductType): Observable<SelectionItem[]> {
    const headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    const url = this.urlBase + 'organizationLeadTimes/' + organizationId + '/' + brandID + '/' + projectProductType;
    return this.http.get<SelectionItem[]>(url, {headers: headers});
  }

  public getOrganizationChannels(organizationId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("organizationChannels/" + organizationId);
  }

  public getOrganizationExpediteCodes(organizationId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("expediteCodes/" + organizationId);
  }

  public getOrganizationTransportationTerms(organizationId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("transportationTerms/" + organizationId);
  }

  public getMarketSegments(): Observable<SelectionItem[]> {
    return this.getSelectionList("marketsegments");
  }

  public getProjectTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("projecttypes");
  }

  public getNewExtendCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("newExtendCodes");
  }

  public getEmulatedOrganizations(): Observable<SelectionItem[]> {
    return this.getSelectionList("emulatedOrganizations");
  }

  public getFactorCodes(): Observable<SelectionItem[]> {
    return this.getSelectionList("factorCodes");
  }

  public getCancelReasons(entityName: string): Observable<SelectionItem[]> {
    return this.getSelectionList("cancelReasons/" + entityName);
  }

  public getAllIecWinLossReasons(): Observable<SelectionItem[]> {
    return this.getSelectionList("iecWinLossReasons");
  }

  public getFpaCompetitors(brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("fpaCompetitors/" + brandID);
  }

  public getPriorities(): Observable<SelectionItem[]> {
    return this.getSelectionList("priorities");
  }

  public getSalesChannels(): Observable<SelectionItem[]> {
    return this.getSelectionList("salesChannels");
  }

  public getDocumentTypes(): Observable<SelectionItem[]> {
    return this.getSelectionList("documentTypes");
  }

  public getMilestones(): Observable<SelectionItem[]> {
    return this.getSelectionList("milestones");
  }

  public getDelegationUsers(): Observable<SelectionItem[]> {
    return this.getSelectionList("delegationUsers");
  }

  public getSfrComplexities(): Observable<SelectionItem[]> {
    return this.getSelectionList("sfrComplexities");
  }

  public getCreditDocumentTypes(brandID: BrandID): Observable<SelectionItem[]> {
    return this.getSelectionList("creditDocumentTypes/" + brandID);
  }

  public getApplicationEngineers(): Observable<SelectionItem[]> {
    return this.getSelectionList('applicationEngineers');
  }

  private sortByStringProperty(first: string, second: string): number {
    if (!first) {
      if (!second) {
        return 0;
      } else {
        return -1;
      }
    } else if (!second) {
      return 1;
    } else {
      let firstLower = first.toLowerCase();
      let secondLower = second.toLowerCase();
      if (firstLower < secondLower) {
        return -1;
      }
      if (firstLower > secondLower) {
        return 1;
      }
      return 0;
    }
  }

  public getRatingScheduleColumns(): Observable<SelectionItem[]> {
    const headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    const url = this.urlBase + 'ratingScheduleColumns';
    return this.http.get<SelectionItem[]>(url, {headers: headers});
  }

  getOrganizationActiveUsers(organizationId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("organizationActiveUsers/" + organizationId);
  }

  getOrganizationEngineeringOffices(organizationId: string): Observable<SelectionItem[]> {
    return this.getSelectionList("organizationEngineeringOffices/" + organizationId);
  }

  public getManagedOutsideEngineeringOffices(): Observable<SelectionItem[]> {
    return this.getSelectionList("managedOutsideEngineeringOffices");
  }

  public getApplicationTypes(): Observable<SelectionItem[]> {
    const headers = new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
    const url = this.urlBase + 'activeApplicationTypes';
    return this.http.get<SelectionItem[]>(url, {headers: headers});
  }

  getSalesManagers(): Observable<SelectionItem[]> {
    return this.getSelectionList("salesManagers");
  }

}
